import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, BadgeIndianRupee, CheckCircle2, CreditCard, Store } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, startPayment } from "@/lib/api";
import { useLang } from "@/i18n";

const PHONE_RE = /^[6-9]\d{9}$/;
const ID_PROOF_VALUES = ["aadhaar", "pan", "voter", "driving", "college", "other"];
const GOAL_VALUES = ["weight-loss", "muscle-gain", "general", "other"];
const SLOT_VALUES = ["morning", "evening", "night"];

const EMPTY = {
  name: "", phone: "", email: "", dob: "", gender: "male", address: "",
  id_proof: "aadhaar", id_card_number: "", package_slug: "",
  height_feet: "", height_inches: "", weight: "", fitness_goal: "general", fitness_goal_other: "",
  preferred_slot: "morning", emergency_name: "", emergency_contact: "", emergency_relation: "",
  medical_notes: "", terms_accepted: false,
};

const SectionHead = ({ label }) => (
  <div className="mt-3 flex items-center gap-2.5 sm:col-span-2">
    <span className="h-4 w-1.5 bg-volt" />
    <p className="font-display text-sm font-bold uppercase tracking-widest text-volt">{label}</p>
  </div>
);

const AdmissionModal = ({ open, onClose, packages, selectedSlug, paymentsEnabled }) => {
  const { lang, t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [createdAdmission, setCreatedAdmission] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, package_slug: selectedSlug || packages[0]?.slug || "" });
      setPhoneError("");
      setCreatedAdmission(null);
      setPaying(false);
    }
  }, [open, selectedSlug, packages]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const selectedPkg = packages.find((p) => p.slug === form.package_slug);
  const slotLabels = { morning: t.slot_morning, evening: t.slot_evening, night: t.slot_night };

  const submit = async (e) => {
    e.preventDefault();
    if (!PHONE_RE.test(form.phone.replace(/[\s-]/g, "")) || !PHONE_RE.test(form.emergency_contact.replace(/[\s-]/g, ""))) {
      setPhoneError(t.error_phone);
      return;
    }
    setPhoneError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        height_feet: form.height_feet === "" ? null : parseInt(form.height_feet, 10),
        height_inches: form.height_inches === "" ? null : parseInt(form.height_inches, 10),
        weight: form.weight === "" ? null : parseFloat(form.weight),
      };
      const { data } = await api.post("/admissions", payload);
      if (paymentsEnabled) {
        setCreatedAdmission(data);
      } else {
        toast.success(t.success_title, { description: t.success_body });
        onClose();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  const payOnline = () => {
    if (!createdAdmission || paying) return;
    setPaying(true);
    startPayment({
      type: "admission",
      admissionId: createdAdmission.id,
      prefill: { name: createdAdmission.name, contact: createdAdmission.phone, email: createdAdmission.email || undefined },
      onSuccess: () => {
        setPaying(false);
        toast.success(t.payment_success);
        onClose();
      },
      onError: () => {
        setPaying(false);
        toast.error(t.payment_failed);
      },
    });
  };

  const payAtDesk = () => {
    toast.success(t.success_title, { description: t.success_body });
    onClose();
  };

  const inputCls =
    "w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-200 focus:border-volt focus:ring-1 focus:ring-volt";
  const labelCls = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-widest text-zinc-400";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/80 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="admission-modal-backdrop"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-zinc-800 bg-panel p-6 sm:rounded-2xl sm:p-9"
            data-testid="admission-modal"
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white" data-testid="admission-form-title">
                  {t.form_title}
                </h3>
                <p className="mt-1.5 text-sm text-zinc-400">{t.form_sub}</p>
              </div>
              <button
                data-testid="admission-modal-close-button"
                onClick={onClose}
                className="rounded-full border border-zinc-700 p-2 text-zinc-400 transition-colors duration-200 hover:border-volt hover:text-volt"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {createdAdmission ? (
              <div className="text-center" data-testid="payment-step">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-volt/15">
                  <CheckCircle2 className="h-9 w-9 text-volt" />
                </span>
                <h4 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-wide text-white" data-testid="payment-step-title">
                  {t.success_title}
                </h4>
                <p className="mt-2 text-sm text-zinc-400">{t.pay_saved_note}</p>

                <div className="mx-auto mt-6 max-w-sm rounded-xl border border-zinc-800 bg-ink p-5 text-left" data-testid="payment-breakdown">
                  <div className="flex justify-between text-sm text-zinc-300">
                    <span>{lang === "en" ? selectedPkg?.name_en : selectedPkg?.name_bn} {lang === "en" ? "package" : "প্যাকেজ"}</span>
                    <span className="font-semibold">₹{createdAdmission.package_price.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="mt-2 flex justify-between text-sm text-zinc-300">
                    <span>{t.admission_fee_short}</span>
                    <span className="font-semibold">₹1,000</span>
                  </div>
                  <div className="mt-3 flex justify-between border-t border-zinc-800 pt-3">
                    <span className="font-display text-sm font-bold uppercase tracking-widest text-white">{t.pay_total}</span>
                    <span className="font-display text-2xl font-extrabold text-volt" data-testid="payment-total">
                      ₹{(createdAdmission.package_price + 1000).toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>

                <button
                  data-testid="pay-online-button"
                  onClick={payOnline}
                  disabled={paying}
                  className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-volt py-4 font-display text-base font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.01] disabled:opacity-60"
                >
                  {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
                  {t.pay_now}
                </button>
                <button
                  data-testid="pay-at-desk-button"
                  onClick={payAtDesk}
                  className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-full border border-zinc-700 py-4 font-display text-base font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-200 hover:border-volt hover:text-volt"
                >
                  <Store className="h-5 w-5" />
                  {t.pay_at_desk_btn}
                </button>
              </div>
            ) : (
              <>
                {selectedPkg && (
                  <div className="mb-6 flex items-center justify-between rounded-xl border border-volt/30 bg-volt/10 px-5 py-3.5" data-testid="admission-selected-package">
                    <span className="font-display text-lg font-bold uppercase tracking-wider text-white">
                      {lang === "en" ? selectedPkg.name_en : selectedPkg.name_bn}
                    </span>
                    <span className="font-display text-2xl font-extrabold text-volt">
                      ₹{selectedPkg.price.toLocaleString("en-IN")}
                      <span className="ml-1 font-mono text-[10px] font-normal uppercase tracking-widest text-zinc-400">
                        {lang === "en" ? selectedPkg.duration_en : selectedPkg.duration_bn}
                      </span>
                    </span>
                  </div>
                )}

                <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="admission-form">
                  <div>
                    <label className={labelCls} htmlFor="adm-package">{t.form_package} *</label>
                    <select id="adm-package" data-testid="admission-package-select" required
                      className={inputCls} value={form.package_slug} onChange={set("package_slug")}>
                      {packages.map((p) => (
                        <option key={p.slug} value={p.slug} data-testid={`admission-package-option-${p.slug}`}>
                          {lang === "en" ? p.name_en : p.name_bn} — ₹{p.price.toLocaleString("en-IN")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-name">{t.form_name} *</label>
                    <input id="adm-name" data-testid="admission-name-input" required minLength={2} maxLength={80}
                      className={inputCls} value={form.name} onChange={set("name")} placeholder="Rahul Sharma" />
                  </div>

                  <SectionHead label={t.sec_member} />

                  <div>
                    <label className={labelCls} htmlFor="adm-phone">{t.form_phone} *</label>
                    <input id="adm-phone" data-testid="admission-phone-input" required
                      className={inputCls} value={form.phone} onChange={set("phone")} placeholder="98XXXXXXXX" inputMode="numeric" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-email">{t.form_email}</label>
                    <input id="adm-email" data-testid="admission-email-input" type="email"
                      className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-dob">{t.form_dob} *</label>
                    <input id="adm-dob" data-testid="admission-dob-input" required type="date"
                      className={`${inputCls} [color-scheme:dark]`} value={form.dob} onChange={set("dob")} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-gender">{t.form_gender} *</label>
                    <select id="adm-gender" data-testid="admission-gender-select" className={inputCls} value={form.gender} onChange={set("gender")}>
                      <option value="male">{t.gender_male}</option>
                      <option value="female">{t.gender_female}</option>
                      <option value="other">{t.gender_other}</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="adm-address">{t.form_address} *</label>
                    <textarea id="adm-address" data-testid="admission-address-input" required minLength={5} rows={2}
                      className={`${inputCls} resize-none`} value={form.address} onChange={set("address")} placeholder="Village / Town, Post Office, District" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-idproof">{t.form_id_proof} *</label>
                    <select id="adm-idproof" data-testid="admission-idproof-select" className={inputCls} value={form.id_proof} onChange={set("id_proof")}>
                      {ID_PROOF_VALUES.map((v, i) => (
                        <option key={v} value={v} data-testid={`admission-idproof-option-${v}`}>{t.id_proofs[i]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-idnum">{t.form_id_number} *</label>
                    <input id="adm-idnum" data-testid="admission-idnumber-input" required minLength={4} maxLength={30}
                      className={inputCls} value={form.id_card_number} onChange={set("id_card_number")} placeholder="XXXX XXXX XXXX" />
                  </div>

                  <SectionHead label={t.sec_fitness} />

                  <div>
                    <label className={labelCls}>{t.form_height}</label>
                    <div className="flex gap-3">
                      <input data-testid="admission-height-feet-input" type="number" min={3} max={8}
                        className={inputCls} value={form.height_feet} onChange={set("height_feet")} placeholder="Feet" />
                      <input data-testid="admission-height-inches-input" type="number" min={0} max={11}
                        className={inputCls} value={form.height_inches} onChange={set("height_inches")} placeholder="Inches" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-weight">{t.form_weight}</label>
                    <input id="adm-weight" data-testid="admission-weight-input" type="number" min={20} max={300} step="0.5"
                      className={inputCls} value={form.weight} onChange={set("weight")} placeholder="65" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="adm-goal">{t.form_goal} *</label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="admission-goal-group">
                      {GOAL_VALUES.map((v, i) => (
                        <button
                          key={v}
                          type="button"
                          data-testid={`admission-goal-${v}-button`}
                          onClick={() => setForm((f) => ({ ...f, fitness_goal: v }))}
                          className={`rounded-lg border px-2 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200 ${
                            form.fitness_goal === v ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                          }`}
                        >
                          {t.goals[i]}
                        </button>
                      ))}
                    </div>
                    {form.fitness_goal === "other" && (
                      <input data-testid="admission-goal-other-input" className={`${inputCls} mt-3`}
                        value={form.fitness_goal_other} onChange={set("fitness_goal_other")} placeholder={t.form_goal_other} />
                    )}
                  </div>

                  <SectionHead label={t.sec_batch} />

                  <div className="sm:col-span-2">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="admission-slot-group">
                      {SLOT_VALUES.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          data-testid={`admission-slot-${slot}-button`}
                          onClick={() => setForm((f) => ({ ...f, preferred_slot: slot }))}
                          className={`rounded-lg border py-3.5 font-display text-sm font-bold uppercase tracking-widest transition-colors duration-200 ${
                            form.preferred_slot === slot
                              ? "border-volt bg-volt/10 text-volt"
                              : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                          }`}
                        >
                          {slotLabels[slot]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <SectionHead label={t.sec_emergency} />

                  <div>
                    <label className={labelCls} htmlFor="adm-ename">{t.form_emergency_name} *</label>
                    <input id="adm-ename" data-testid="admission-emergency-name-input" required minLength={2}
                      className={inputCls} value={form.emergency_name} onChange={set("emergency_name")} placeholder="Parent / Guardian name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-emergency">{t.form_emergency} *</label>
                    <input id="adm-emergency" data-testid="admission-emergency-input" required
                      className={inputCls} value={form.emergency_contact} onChange={set("emergency_contact")} placeholder="98XXXXXXXX" inputMode="numeric" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="adm-relation">{t.form_relation} *</label>
                    <input id="adm-relation" data-testid="admission-relation-input" required minLength={2} maxLength={40}
                      className={inputCls} value={form.emergency_relation} onChange={set("emergency_relation")} placeholder="Father / Mother / Spouse" />
                  </div>

                  <SectionHead label={t.sec_medical} />

                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="adm-medical">{t.form_medical}</label>
                    <textarea id="adm-medical" data-testid="admission-medical-input" rows={2}
                      className={`${inputCls} resize-none`} value={form.medical_notes} onChange={set("medical_notes")} placeholder="..." />
                  </div>

                  <SectionHead label={t.sec_terms} />

                  <div className="sm:col-span-2">
                    <ol className="max-h-36 list-decimal space-y-1.5 overflow-y-auto rounded-lg border border-zinc-800 bg-ink p-4 pl-9 text-xs leading-relaxed text-zinc-400" data-testid="admission-terms-list">
                      {t.terms_points.map((point, i) => (
                        <li key={i} className={i === 7 ? "font-semibold text-red-400" : ""}>{point}</li>
                      ))}
                    </ol>
                    <label className="mt-3 flex cursor-pointer items-start gap-3" data-testid="admission-terms-agree-label">
                      <input
                        type="checkbox"
                        required
                        data-testid="admission-terms-checkbox"
                        checked={form.terms_accepted}
                        onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFE600]"
                      />
                      <span className="text-xs text-zinc-300">{t.terms_agree}</span>
                    </label>
                  </div>

                  {phoneError && (
                    <p className="sm:col-span-2 text-sm text-red-400" data-testid="admission-phone-error">{phoneError}</p>
                  )}

                  {!paymentsEnabled && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500 sm:col-span-2" data-testid="admission-pay-note">
                      <BadgeIndianRupee className="h-4 w-4 text-volt" />
                      {t.form_pay_note}
                    </div>
                  )}

                  <button
                    data-testid="admission-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="mt-2 flex items-center justify-center gap-2 rounded-full bg-volt py-4 font-display text-base font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.01] disabled:opacity-60 sm:col-span-2"
                  >
                    {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                    {submitting ? t.form_submitting : t.form_submit}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdmissionModal;
